<template>
    <div class="discount-code">
        <ButtonLink
            size="m"
            icon="Chevron"
            :onClick="toggleIsExpanded"
            :iconRotate="isExpanded ? 180 : 0">
            {{
                !isExpanded
                    ? t({ key: 'shared.discount_code' })
                    : t({ key: 'shared.hide_discount_code' })
            }}
        </ButtonLink>

        <Notification
            v-if="status.type === DiscountCodeStatusType.Success && status.message"
            icon="Check"
            radius="m"
            variant="positive"
            fullWidth>
            {{ status.message }}
        </Notification>

        <TransitionExpand>
            <div v-if="isExpanded" class="discount-code-content">
                <FormV2
                    id="form-discount-code"
                    class="discount-code-content-form"
                    :onSubmit="handleSubmit"
                    :initialValues="initialValues"
                    :validationSchema="validationSchema">
                    <TextboxField
                        name="code"
                        v-model="inputCode"
                        :customErrors="errors ?? undefined"
                        :input="{
                            label: t({
                                key: 'shared.fill_in_code',
                            }),
                            disabled: isSubmitting,
                            onChange: handleInputChange,
                        }" />
                    <ButtonPrimary type="submit" size="l" :loading="isSubmitting">
                        {{ t({ key: 'shared.add' }) }}
                    </ButtonPrimary>

                    <small>
                        {{ t({ key: 'shared.got_gift_card' }) }}
                    </small>
                </FormV2>
            </div>
        </TransitionExpand>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { ref, defineComponent, computed } from 'vue';
    import { useStore } from '@/store';
    import { endpoints } from '@/api/endpoints';
    import { logAnalyticsAddCampaign } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import {
        isRequired,
        isValidCharacters,
    } from '@ui/components/FormV2/Validations/functions';
    import { DiscountCodeStatus, DiscountCodeStatusType } from './DiscountCode.interfaces';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { handleCustomError } from '@/config/errorHandling';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import {
        logStatsigCheckoutDiscountCodeApplied,
        logStatsigCheckoutDiscountCodeError,
        logStatsigCheckoutDiscountCodeOpen,
    } from '@/tracking/statsig/checkout.functions';

    export default defineComponent({
        name: 'DiscountCode',
        components: {
            TransitionExpand,
            TextboxField,
            ButtonLink,
            ButtonPrimary,
            FormV2,
            Notification,
        },
        methods: { t },
        setup() {
            const store = useStore();
            const status = ref<DiscountCodeStatus>({
                type: DiscountCodeStatusType.Initial,
                message: null,
            });

            const inputCode = ref<string | undefined>(undefined);
            const isExpanded = ref<boolean>(false);

            const initialValues = computed<FormValues>(() => ({
                code: undefined,
            }));

            const validationSchema = computed<ValidationSchema>(() => ({
                code: [isValidCharacters, isRequired],
            }));

            const isSubmitting = computed<boolean>(
                () => status.value.type === DiscountCodeStatusType.Submitting
            );

            const hasSuccess = computed<boolean>(
                () => status.value.type === DiscountCodeStatusType.Success
            );

            const hasError = computed<boolean>(
                () => status.value.type === DiscountCodeStatusType.Error
            );

            const errors = computed<string[] | undefined>(() => {
                return hasError.value && status.value.message
                    ? [status.value.message]
                    : undefined;
            });

            function toggleIsExpanded() {
                isExpanded.value = !isExpanded.value;

                if (isExpanded.value) {
                    resetStatus();
                    trackClarity(ClarityEvent.CHECKOUT_DISCOUNT_CODE_OPEN);
                    logStatsigCheckoutDiscountCodeOpen();
                } else {
                    inputCode.value = undefined;
                }
            }

            async function handleSubmit({ code }: FormValues) {
                try {
                    if (code && status.value.type !== DiscountCodeStatusType.Submitting) {
                        setSubmitting();

                        const response = await endpoints.checkout.RedeemDiscountCode(code);

                        if (response.Discount === null) {
                            setError(response.ErrorMessage);

                            logStatsigCheckoutDiscountCodeError(code);
                        } else {
                            setSuccess(t({ key: 'shared.code_is_valid' }));

                            store.dispatch('cart/setCampaignDiscount', response.Discount);

                            logAnalyticsAddCampaign(response);
                            logStatsigCheckoutDiscountCodeApplied(
                                code,
                                response.Discount.Amount
                            );
                        }
                    }
                } catch (error) {
                    handleCustomError(`Error adding discount code to booking: ${error}`);
                    setError(t({ key: 'shared.discount_code_error' }));
                }
            }

            function setSubmitting() {
                status.value.message = null;
                status.value.type = DiscountCodeStatusType.Submitting;
            }

            function setSuccess(text?: string | null) {
                status.value.type = DiscountCodeStatusType.Success;
                status.value.message = text || null;
                inputCode.value = undefined;
                isExpanded.value = false;
            }

            function setError(text?: string | null) {
                status.value.message = text || null;
                status.value.type = DiscountCodeStatusType.Error;
            }

            function resetStatus() {
                status.value.type = DiscountCodeStatusType.Initial;
                status.value.message = null;
            }

            function handleInputChange() {
                (hasError.value || hasSuccess.value) && resetStatus();
            }

            return {
                inputCode,
                isExpanded,
                isSubmitting,
                toggleIsExpanded,
                handleSubmit,
                handleInputChange,
                initialValues,
                validationSchema,
                status,
                errors,
                DiscountCodeStatusType,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './DiscountCode.scss';
</style>
